import { t } from '@lingui/core/macro';
import { Plugin } from 'ckeditor5';
import { v4 as uuid } from 'uuid';
import { REMOVE_CONDITION_COMMAND, RemoveConditionCommand } from './RemoveConditionCommand';
import { RENAME_CONDITION_REF_COMMAND, RenameConditionRefCommand } from './RenameConditionRefCommand';
import { SET_CONDITION_COMMAND, SetConditionCommand } from './SetConditionCommand';
import { ConditionState } from './State';
import { InsertConditionCommand } from './insertCommand';
import { CONDITION_INLINE_PREFIX, buildMarkerName, parseMarkerName } from './markerName';
import { resolveTemplateRefFallback } from '../templateRefs';
import './style.css';
/**
 * Compact text for a marker pill. Long enough to identify the condition at a glance, short enough
 * not to shove the prose around.
 */
function conditionPillLabel(options) {
    const { state, label, predicate, ref, choiceKey } = options;
    if (state === 'unconfigured') {
        return t({ message: 'set up condition', comment: 'Condition with no configuration', context: 'editor' });
    }
    if (state === 'broken') {
        return t({
            message: `Unlinked: ${ref}`,
            comment: 'A unlinked condition, it has no question attached',
            context: 'editor',
        });
    }
    const prefix = predicate === 'assume' ? 'if' : 'unless';
    const target = choiceKey ? `${label} = ${choiceKey}` : label;
    return `${prefix} ${target}`;
}
export class ConditionsSchema extends Plugin {
    static get pluginName() {
        return 'ConditionsSchema';
    }
    init() {
        this.state = this.editor.plugins.get(ConditionState);
        this.defineConverters();
        this.defineCommands();
    }
    defineCommands() {
        const { editor } = this;
        editor.commands.add(InsertConditionCommand.pluginName, new InsertConditionCommand(editor));
        editor.commands.add(SET_CONDITION_COMMAND, new SetConditionCommand(editor));
        editor.commands.add(REMOVE_CONDITION_COMMAND, new RemoveConditionCommand(editor));
        editor.commands.add(RENAME_CONDITION_REF_COMMAND, new RenameConditionRefCommand(editor));
    }
    /**
     * Re-renders every condition. Call after the question list changes so labels and states
     * (linked / broken / unconfigured) catch up — the resolver is only consulted during downcast.
     */
    refreshLabels() {
        const { editor } = this;
        const markerNames = Array.from(editor.model.markers.getMarkersGroup(CONDITION_INLINE_PREFIX)).map((marker) => marker.name);
        markerNames.forEach((markerName) => {
            if (editor.model.markers.has(markerName)) {
                editor.editing.reconvertMarker(markerName);
            }
        });
    }
    /**
     * Sets the question list's label resolver.
     * Call `refreshLabels()` afterwards to re-render the pills.
     */
    setRefResolver(resolver) {
        this.refResolver = resolver;
    }
    get resolveRef() {
        return this.refResolver || this.editor.config.get('conditions.resolveRef') || resolveTemplateRefFallback;
    }
    defineConverters() {
        const { editor } = this;
        // Map of condition names to instance IDs for upcast conversion.
        // In the HTML source conditions can share the same name, this is however not supported by
        // CKEditor markers. To work around this we generate a unique instance ID for each condition on
        // upcast. Note this breaks for two *nested* conditions sharing a name — the inner
        // <condition-end> consumes the outer's entry. The condition editor warns about that case.
        const conditionInstanceMap = new Map();
        /**
         * UPCAST: HTML → Model
         * Converts <condition-start> tags from HTML
         * into condition markers in the model.
         */
        editor.conversion.for('upcast').elementToMarker({
            view: 'condition-start',
            model: (viewElement) => {
                const name = viewElement.getAttribute('name');
                const instanceId = uuid();
                const markerName = buildMarkerName(name, instanceId);
                conditionInstanceMap.set(name, instanceId);
                this.state.addCondition(markerName);
                return markerName;
            },
        });
        /**
         * UPCAST: HTML → Model
         * Converts <condition-end> tags from HTML
         * into condition markers in the model.
         */
        editor.conversion.for('upcast').elementToMarker({
            view: 'condition-end',
            model: (viewElement) => {
                const name = viewElement.getAttribute('name');
                const instanceId = conditionInstanceMap.get(name);
                conditionInstanceMap.delete(name);
                return buildMarkerName(name, instanceId);
            },
        });
        /**
         * EDITING DOWNCAST: Model → View (Editor)
         * Converts condition markers in the model into condition pills in the view. The pill carries
         * the resolved question label and link state, which the stylesheet renders.
         */
        editor.conversion.for('editingDowncast').markerToElement({
            model: 'condition-inline',
            view: (markerData, { writer }) => {
                const parsed = parseMarkerName(markerData.markerName);
                const { predicate = 'assume', ref = '', choiceKey } = parsed || {};
                const { state, label } = this.resolveRef(ref);
                const className = markerData.isOpening
                    ? 'condition-label condition-label--start'
                    : 'condition-label condition-label--end';
                return writer.createUIElement('span', {
                    class: className,
                    'data-condition-id': (parsed === null || parsed === void 0 ? void 0 : parsed.name) || '',
                    'data-condition-label': conditionPillLabel({ state, label, predicate, ref, choiceKey }),
                    'data-condition-state': state,
                    'data-marker-name': markerData.markerName,
                    'data-start': markerData.isOpening,
                });
            },
        });
        /**
         * DATA DOWNCAST: Model → HTML
         * Converts condition markers in the model
         * into <condition-start> and <condition-end> tags in HTML.
         *
         * The instance id is dropped here — the name is written back verbatim, exactly as it was read.
         */
        editor.conversion.for('dataDowncast').markerToElement({
            model: 'condition-inline',
            view: (markerData, { writer }) => {
                var _a;
                const name = ((_a = parseMarkerName(markerData.markerName)) === null || _a === void 0 ? void 0 : _a.name) || '';
                if (markerData.isOpening) {
                    return writer.createEmptyElement('condition-start', { name });
                }
                return writer.createEmptyElement('condition-end', { name });
            },
        });
    }
}
